import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { GetAccessTokenWithCodeResponse } from '../types/auth0-types';
import { AUTH0_LIB_CONFIG, AUTH0_REST_API_URL } from '../types/auth0-config.token';
import { AuthenticationService } from '../authentication-service/authentication.service';

const OAUTH_STATE_KEY = 'oauth_state';
const ACCESS_TOKEN_KEY = 'access_token';

@Injectable()
export class Auth0Service {
    readonly #router = inject(Router);
    readonly #http = inject(HttpClient);
    readonly #authService = inject(AuthenticationService);
    readonly #config = inject(AUTH0_LIB_CONFIG);
    readonly #restApiUrl = inject(AUTH0_REST_API_URL);

    hasError = signal(false);
    errorMessage = signal('');

    login(): void {
        const { clientId, domain } = this.#config.auth0Config;
        const baseUri = new URL(document.baseURI);
        let callbackUrl = baseUri.origin + baseUri.pathname.replace(/\/$/, '') + '/auth/callback';

        const redirectUrl = this.#router.parseUrl(this.#router.url).queryParams['redirectUrl'];
        if (redirectUrl) {
            const decoded = decodeURIComponent(redirectUrl);
            if (decoded.startsWith('/') && !decoded.startsWith('//')) {
                callbackUrl += `?redirectUrl=${redirectUrl}`;
            }
        }

        const state = this.#generateState();
        sessionStorage.setItem(OAUTH_STATE_KEY, state);

        globalThis.location.href = `https://${domain}/authorize?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(callbackUrl)}&scope=openid%20profile%20email&state=${encodeURIComponent(state)}`;
    }

    authorizeWithCode(code: string, state: string | undefined): void {
        const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_STATE_KEY);

        if (!state || !storedState || state !== storedState) {
            this.errorMessage.set('Authentication failed: invalid state parameter. Please try logging in again.');
            this.hasError.set(true);
            return;
        }

        const baseUri = new URL(document.baseURI);
        const redirectUri = baseUri.origin + baseUri.pathname.replace(/\/$/, '') + '/auth/callback';
        this.#http.post<GetAccessTokenWithCodeResponse>(`${this.#restApiUrl}/auth/code`, { code, redirectUri }).subscribe({
            next: (res) => {
                if (res?.accessToken) {
                    localStorage.setItem(ACCESS_TOKEN_KEY, res.accessToken);
                    this.#router.navigate([this.getRedirectUrl() ?? '/']);
                }
            },
            error: () => {
                this.errorMessage.set('Authentication failed. Please try logging in again.');
                this.hasError.set(true);
            },
        });
    }

    logout(): void {
        const { clientId, domain } = this.#config.auth0Config;
        const returnToUrl = new URL(globalThis.location.href);
        returnToUrl.search = '';
        returnToUrl.pathname = '/';
        this.#authService.clearSession();
        sessionStorage.removeItem(OAUTH_STATE_KEY);

        if (this.#config.logoutStorageClear) {
            this.#config.logoutStorageClear.forEach((key) => localStorage.removeItem(key));
        } else {
            localStorage.clear();
        }

        globalThis.location.href = `https://${domain}/v2/logout?client_id=${encodeURIComponent(clientId)}&returnTo=${encodeURIComponent(returnToUrl.toString())}`;
    }

    getRedirectUrl(): string | null {
        const redirectParam = this.#router.parseUrl(this.#router.url).queryParams['redirectUrl'];
        if (!redirectParam) return null;
        if (redirectParam.startsWith('/') && !redirectParam.startsWith('//')) return redirectParam;
        return null;
    }

    #generateState(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
}
